import React, { useState, useEffect } from 'react';
import { hasPermission, getUserType, getCurrentUser } from '../services/authService';
import api from '../services/api';
import DocumentosModal from './DocumentosModal';
import AnalisisPdfModal from './AnalisisPdfModal';
import {
  ExclamationTriangleIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  DocumentIcon,
  DocumentTextIcon,
  FaceFrownIcon,
} from '@heroicons/react/24/outline';
import { Button, IconButton, Card, FormField, Input, Select, LoadingState, EmptyState } from './ui';
import { getEnsayosMedio, EnsayoMedio } from '../services/ensayoMedioService';
import { getClientes, Cliente } from '../services/clienteService';
import { getPlantasByClienteId, Planta } from '../services/plantaService';
import { formatFecha } from '../utils/fecha';

interface ResultadoBusqueda {
  id: number;
  numAnalisis: string;
  origen: string;
  destino: string;
  fecha: string;
  fechaFinalizacion?: string;
  tienePdf: boolean;
  /** 1 = finalizado. Un análisis pendiente todavía no tiene fecha de cierre. */
  estado: number;
}

interface ClienteInfo {
  id: number;
  nombre: string;
  razon_social: string;
}

interface LaboratorioInfo {
  id: number;
  nombre: string;
}

// Extender la interfaz UserInfo para incluir id_grupo
interface ExtendedUser {
  id: number;
  username?: string;
  nombre?: string;
  apellido?: string;
  email?: string;
  tipo?: string;
  tabla_grupo?: string;
  id_grupo?: number;
  info_adicional?: any;
}

const Busqueda: React.FC = () => {
  // Verificar si el usuario tiene permiso para realizar búsquedas
  const tienePermisoBusqueda = hasPermission('p_busqueda');
  
  // Obtener tipo de usuario e información adicional
  const userType = getUserType();
  const currentUser = getCurrentUser();
  const [userInfo, setUserInfo] = useState<any>(null);
  
  // Estados para los filtros de búsqueda
  const [numAnalisis, setNumAnalisis] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [analito, setAnalito] = useState('');
  const [medio, setMedio] = useState('');
  // El cliente se elige de una lista: hace falta su id para traer sus plantas.
  const [clienteId, setClienteId] = useState<number | ''>('');
  const [planta, setPlanta] = useState('');
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [plantas, setPlantas] = useState<Planta[]>([]);
  const [cargandoPlantas, setCargandoPlantas] = useState(false);
  const [laboratorio, setLaboratorio] = useState('');
  
  // Estado para los resultados
  const [resultados, setResultados] = useState<ResultadoBusqueda[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingUserInfo, setLoadingUserInfo] = useState(true);
  const [busquedaRealizada, setBusquedaRealizada] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Agregar estado para el modal de documentos
  const [documentosModalOpen, setDocumentosModalOpen] = useState(false);
  const [selectedAnalisisId, setSelectedAnalisisId] = useState<number | null>(null);

  // Agregar estado para el modal PDF
  const [pdfModalOpen, setPdfModalOpen] = useState(false);
  const [selectedPdfAnalisisId, setSelectedPdfAnalisisId] = useState<number | null>(null);

  // Los medios se toman del catálogo para que el valor del filtro sea el mismo
  // identificador que guarda cada muestra; así la búsqueda devuelve todos los
  // análisis que contienen alguna muestra de ese medio.
  const [medios, setMedios] = useState<EnsayoMedio[]>([]);

  useEffect(() => {
    getEnsayosMedio()
      .then(setMedios)
      .catch(err => console.error('Error al cargar medios:', err));
  }, []);

  // El listado de clientes sólo lo necesita quien puede filtrar por cliente; un
  // usuario cliente ya tiene el suyo fijado por su cuenta.
  useEffect(() => {
    if (userType === 'cliente') return;
    getClientes()
      .then(setClientes)
      .catch(err => console.error('Error al cargar clientes:', err));
  }, [userType]);

  // Plantas del cliente seleccionado. Al cambiar de cliente se descarta la
  // planta elegida, que pertenecía al anterior.
  useEffect(() => {
    if (clienteId === '') {
      setPlantas([]);
      setPlanta('');
      return;
    }
    let vigente = true;
    setCargandoPlantas(true);
    getPlantasByClienteId(Number(clienteId))
      .then(datos => {
        if (vigente) setPlantas(datos);
      })
      .catch(err => console.error('Error al cargar plantas:', err))
      .finally(() => {
        if (vigente) setCargandoPlantas(false);
      });
    return () => {
      vigente = false;
    };
  }, [clienteId]);

  // Obtener información del usuario al cargar el componente
  useEffect(() => {
    const fetchUserInfo = async () => {
      if (!currentUser) return;
      
      try {
        setLoadingUserInfo(true);
        
        // Tratar currentUser como ExtendedUser para acceder a id_grupo
        const user = currentUser as ExtendedUser;
        
        if (userType === 'cliente' && user.id_grupo) {
          try {
            // Verificar si ya tenemos la información en el estado para evitar consultas repetidas
            if (!userInfo || userInfo.id !== user.id_grupo) {
              console.log(`Consultando información del cliente con ID ${user.id_grupo}`);
              const response = await api.get(`/clientes/${user.id_grupo}`);
              setUserInfo(response.data);
              setClienteId(response.data.id); // Sus análisis y sus plantas
            }
          } catch (err) {
            console.error('Error al obtener información del cliente:', err);
          }
        } 
        else if (userType === 'laboratorio' && user.id_grupo) {
          try {
            // Verificar si ya tenemos la información en el estado para evitar consultas repetidas
            if (!userInfo || userInfo.id !== user.id_grupo) {
              console.log(`Consultando información del laboratorio con ID ${user.id_grupo}`);
              const response = await api.get(`/laboratorios/${user.id_grupo}`);
              setUserInfo(response.data);
              setLaboratorio(response.data.nombre); // Rellenar automáticamente el campo laboratorio
            }
          } catch (err) {
            console.error('Error al obtener información del laboratorio:', err);
          }
        }
      } catch (error) {
        console.error('Error general al obtener información del usuario:', error);
      } finally {
        setLoadingUserInfo(false);
      }
    };

    fetchUserInfo();
    
    // Función de limpieza para evitar problemas de memoria
    return () => {
      // Limpiar variables si es necesario
    };
  }, [currentUser, userType]); // Eliminar userInfo de las dependencias para evitar bucles

  // Función para realizar la búsqueda
  const buscar = async (event: React.FormEvent) => {
    // Prevenir el comportamiento por defecto del formulario
    if (event) {
      event.preventDefault();
    }
    
    // Verificar token antes de intentar la búsqueda
    const token = localStorage.getItem('token');
    if (!token) {
      setErrorMessage('No hay una sesión activa. Por favor, inicie sesión nuevamente.');
      return;
    }
    
    // Si no tiene permiso, no permitir la búsqueda
    if (!tienePermisoBusqueda) {
      return;
    }
    
    setIsLoading(true);
    setBusquedaRealizada(true);
    setErrorMessage('');
    
    try {
      // Construir parámetros de búsqueda
      const params: any = {
        numAnalisis,
        fechaDesde,
        fechaHasta,
        analito,
        medio
      };
      
      // Aplicar filtros según el tipo de usuario
      if (userType === 'cliente' && userInfo) {
        params.id_origen = userInfo.id; // Filtrar por ID del cliente
      } else if (userType === 'laboratorio' && userInfo) {
        params.id_lab = userInfo.id; // Filtrar por ID del laboratorio
      } else {
        // Es administrador u otro tipo, incluir parámetros de filtrado manual
        if (clienteId !== '') params.id_origen = clienteId;
        if (laboratorio) params.laboratorio = laboratorio;
      }
      
      if (planta) params.planta = planta;
      
      console.log('Enviando búsqueda con parámetros:', params);
      
      // Realizar la llamada a la API con el token explícito como respaldo
      const response = await api.get('/analisis/search', { 
        params,
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('Respuesta de búsqueda recibida:', response.status);
      setResultados(response.data);
      
      // Si no hay resultados, mostrar mensaje
      if (response.data.length === 0) {
        setErrorMessage('No se encontraron resultados para la búsqueda');
      }
    } catch (error: any) {
      console.error('Error al realizar la búsqueda:', error);
      
      // Mostrar mensaje de error específico
      if (error.response) {
        if (error.response.status === 401) {
          setErrorMessage('La sesión ha expirado. Serás redirigido a la página de inicio de sesión.');
          // No redirigir aquí, ya lo maneja el interceptor
        } else if (error.response.status === 403) {
          setErrorMessage('No tienes permiso para realizar esta búsqueda. Por favor, contacta al administrador.');
        } else {
          setErrorMessage(`Error: ${error.response.data?.message || 'Ocurrió un error al buscar'}`);
        }
      } else {
        setErrorMessage('Ocurrió un error al conectar con el servidor');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Determinar si el campo cliente debe estar deshabilitado
  const isClienteDisabled = !tienePermisoBusqueda || userType === 'cliente';
  
  // Determinar si el campo laboratorio debe estar deshabilitado
  const isLaboratorioDisabled = !tienePermisoBusqueda || userType === 'laboratorio';

  // Mensaje de advertencia si el usuario no tiene permiso
  const mensajePermiso = !tienePermisoBusqueda && (
    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-amber-700">
            No tienes permiso para realizar búsquedas. Los campos han sido deshabilitados.
          </p>
        </div>
      </div>
    </div>
  );

  // Mensaje informativo sobre filtros automáticos según el tipo de usuario
  const mensajeFiltroAutomatico = (userType === 'cliente' || userType === 'laboratorio') && (
    <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <InformationCircleIcon className="h-5 w-5 text-blue-400" />
        </div>
        <div className="ml-3">
          <p className="text-sm text-blue-700">
            {userType === 'cliente'
              ? 'Como cliente, solo puedes buscar análisis asociados a tu cuenta.'
              : 'Como laboratorio, solo puedes buscar análisis asociados a tu laboratorio.'}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {mensajePermiso}
      {mensajeFiltroAutomatico}
      
      <Card className="mb-6" padded={false}>
        <div className="p-4 bg-brand-50 border-b border-brand-100">
          <h3 className="font-bold text-brand-800">Parámetros de búsqueda</h3>
        </div>

        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
            <FormField label="Número de Análisis" htmlFor="numAnalisis">
              <Input
                id="numAnalisis"
                type="text"
                value={numAnalisis}
                onChange={(e) => setNumAnalisis(e.target.value)}
                placeholder="Ej: A-2023-001"
                disabled={!tienePermisoBusqueda}
              />
            </FormField>

            <FormField label="Fecha análisis: Desde" htmlFor="fechaDesde">
              <Input
                id="fechaDesde"
                type="date"
                value={fechaDesde}
                onChange={(e) => setFechaDesde(e.target.value)}
                disabled={!tienePermisoBusqueda}
              />
            </FormField>

            <FormField label="Fecha análisis: Hasta" htmlFor="fechaHasta">
              <Input
                id="fechaHasta"
                type="date"
                value={fechaHasta}
                onChange={(e) => setFechaHasta(e.target.value)}
                disabled={!tienePermisoBusqueda}
              />
            </FormField>

            <FormField label="Analito" htmlFor="analito">
              <Input
                id="analito"
                type="text"
                value={analito}
                onChange={(e) => setAnalito(e.target.value)}
                placeholder="Ingrese analito"
                disabled={!tienePermisoBusqueda}
              />
            </FormField>

            <FormField label="Medio" htmlFor="medio">
              <Select
                id="medio"
                value={medio}
                onChange={(e) => setMedio(e.target.value)}
                disabled={!tienePermisoBusqueda}
              >
                <option value="">Seleccione medio...</option>
                {medios.map((opcion) => (
                  <option key={opcion.id} value={opcion.id}>{opcion.medio}</option>
                ))}
              </Select>
            </FormField>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            <FormField label="Cliente" htmlFor="cliente">
              {userType === 'cliente' ? (
                <Input id="cliente" type="text" value={userInfo?.nombre || ''} disabled readOnly />
              ) : (
                <Select
                  id="cliente"
                  value={clienteId}
                  onChange={(e) => setClienteId(e.target.value === '' ? '' : Number(e.target.value))}
                  disabled={isClienteDisabled}
                >
                  <option value="">Todos los clientes</option>
                  {clientes.map((opcion) => (
                    <option key={opcion.id} value={opcion.id}>
                      {opcion.nombre}
                      {opcion.razon_social ? ' - ' + opcion.razon_social : ''}
                    </option>
                  ))}
                </Select>
              )}
              {userType === 'cliente' && (
                <span className="text-xs text-slate-500 mt-1 block">Cliente seleccionado automáticamente según tu cuenta</span>
              )}
            </FormField>

            <FormField label="Planta" htmlFor="planta">
              <Select
                id="planta"
                value={planta}
                onChange={(e) => setPlanta(e.target.value)}
                disabled={clienteId === '' || !tienePermisoBusqueda}
              >
                <option value="">Todas las plantas</option>
                {plantas.map((opcion) => (
                  <option key={opcion.id} value={opcion.id}>
                    {opcion.denominacion}
                  </option>
                ))}
              </Select>
              {cargandoPlantas && <span className="text-xs text-slate-500 mt-1 block">Cargando plantas...</span>}
              {clienteId !== '' && !cargandoPlantas && plantas.length === 0 && (
                <span className="text-xs text-slate-500 mt-1 block">Este cliente no tiene plantas cargadas</span>
              )}
            </FormField>

            <FormField label="Laboratorio" htmlFor="laboratorio">
              <Input
                id="laboratorio"
                type="text"
                value={laboratorio}
                onChange={(e) => setLaboratorio(e.target.value)}
                placeholder="Ingrese laboratorio"
                disabled={isLaboratorioDisabled}
              />
              {userType === 'laboratorio' && (
                <span className="text-xs text-slate-500 mt-1 block">Laboratorio seleccionado automáticamente según tu cuenta</span>
              )}
            </FormField>
          </div>

          <div className="mt-6 flex justify-center">
            <Button
              onClick={buscar}
              disabled={!tienePermisoBusqueda}
              loading={isLoading}
              icon={<MagnifyingGlassIcon className="w-5 h-5" />}
            >
              Buscar
            </Button>
          </div>
        </div>
      </Card>

      {/* Resultados de la búsqueda */}
      {busquedaRealizada && (
        <Card padded={false}>
          <div className="p-4 bg-slate-50 border-b border-slate-200">
            <h3 className="font-bold text-slate-800">Resultado de la búsqueda</h3>
          </div>

          {isLoading ? (
            <LoadingState label="Buscando..." />
          ) : resultados.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-slate-100">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">#</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Análisis n°</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Origen</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Destino</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fecha de Carga</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">Fecha Finalización</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">PDF</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {resultados.map((resultado, index) => (
                    <tr key={resultado.id} className="hover:bg-slate-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{index + 1}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-slate-900">{resultado.numAnalisis}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{resultado.origen}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{resultado.destino}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">{formatFecha(resultado.fecha)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        {/* Un análisis sin cerrar figura como pendiente, que es
                            la información útil en esa columna. */}
                        {resultado.fechaFinalizacion
                          ? formatFecha(resultado.fechaFinalizacion)
                          : <span className="text-amber-700">Pendiente</span>}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                        <div className="flex space-x-1">
                          <IconButton
                            onClick={() => {
                              setSelectedPdfAnalisisId(resultado.id);
                              setPdfModalOpen(true);
                            }}
                            disabled={!tienePermisoBusqueda}
                            label="Ver PDF"
                            tone="danger"
                            icon={<DocumentTextIcon className="w-5 h-5" />}
                          />

                          <IconButton
                            onClick={() => {
                              setSelectedAnalisisId(resultado.id);
                              setDocumentosModalOpen(true);
                            }}
                            disabled={!tienePermisoBusqueda}
                            label="Documentos"
                            tone="brand"
                            icon={<DocumentIcon className="w-5 h-5" />}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState
              icon={<FaceFrownIcon className="h-6 w-6" />}
              title={errorMessage || 'No se encontraron resultados para tu búsqueda.'}
            />
          )}
        </Card>
      )}

      {/* Modal para gestionar documentos */}
      {documentosModalOpen && selectedAnalisisId && (
        <DocumentosModal
          analisisId={selectedAnalisisId}
          isOpen={documentosModalOpen}
          onClose={() => setDocumentosModalOpen(false)}
        />
      )}

      {/* Modal para vista previa del PDF */}
      {pdfModalOpen && selectedPdfAnalisisId && (
        <AnalisisPdfModal
          analisisId={selectedPdfAnalisisId}
          isOpen={pdfModalOpen}
          onClose={() => setPdfModalOpen(false)}
        />
      )}
    </div>
  );
};

export default Busqueda; 